import startOfMonth from 'date-fns/startOfMonth';
import getDay from 'date-fns/getDay';
import getDate from 'date-fns/getDate';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import getMonth from 'date-fns/getMonth';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';

import _get from 'lodash/get';
import _range from 'lodash/range';
import _set from 'lodash/set';
import _forEach from 'lodash/forEach';
import _defaultTo from 'lodash/defaultTo';
import _includes from 'lodash/includes';
import _some from 'lodash/some';

/**
 * Get the date of the first date of the calendar.
 *
 * @param {*} dt Any date in the month of the calendar is displaying.
 */
export const getCalendarFirstDay = (dt = startOfDay(new Date())) => {
    const firstDayOfMonth = startOfMonth(dt);
    const firstDayOfMonthDayIdx = getDay(firstDayOfMonth);
    const firstDay = subDays(firstDayOfMonth, firstDayOfMonthDayIdx); // back count to the first day of the calendar 25 days
    return firstDay;
};

/**
 * Generate the data for monthly calendar.
 *
 * Here's the format:
 * {
 *    w0: { dayIds: ['w0d0', 'w0d1', 'w0d2', 'w0d3', 'w0d4', 'w0d5', 'w0d6'] },
 *    w1: { .... },
 *    ....
 *    w0d0: { dt, active },
 *    w0d1: { dt, active },
 *    ....
 *    lastDayId: 'w?d6'
 *  }
 *
 * @param {*} firstDay Date of the first day of the calendar.
 */
export const createMonthlyCalendarData = firstDay => {
    let calendarData = {};

    const weekIdxList = _range(6); // number of weeks in calendar
    const dayIdxList = _range(7); // number of days in a week

    const firstDayMonth = getMonth(firstDay);
    const firstDayDate = getDate(firstDay);
    const currentMonth = firstDayDate === 1 ? firstDayMonth : getMonth(addMonths(firstDay, 1));
    let idx = 0;

    _forEach(weekIdxList, weekIdx => {
        _forEach(dayIdxList, dayIdx => {
            const dayId = `w${weekIdx}d${dayIdx}`;
            const dt = addDays(firstDay, idx++);

            // generate the week object with the id's of the days in that week
            _set(calendarData, `w${weekIdx}.dayIds[${dayIdx}]`, dayId);

            // generate the day objects
            _set(calendarData, dayId, {
                dt,
                active: currentMonth === getMonth(dt),
                isLastDayOfWeek: dayIdx === 6,
            });
        });
    });

    // set the last day id (check if there are 6, or 5, or 4 weeks and pick the last day of the last week)
    const lastDayId = _get(calendarData, 'w5d0.active') ? 'w5d6' : _get(calendarData, 'w4d0.active') ? 'w4d6' : 'w3d6';
    _set(calendarData, 'lastDayId', lastDayId);

    return calendarData;
};

export const getFirstDayOf6thWeek = calendarData => {
    return _get(calendarData, 'w5d0.dt');
};

export const createWeeklyCalendarData = (dayOfWeek = new Date()) => {
    const firstDayOfWeek = startOfWeek(dayOfWeek);

    return {
        day0: firstDayOfWeek,
        day1: addDays(firstDayOfWeek, 1),
        day2: addDays(firstDayOfWeek, 2),
        day3: addDays(firstDayOfWeek, 3),
        day4: addDays(firstDayOfWeek, 4),
        day5: addDays(firstDayOfWeek, 5),
        day6: addDays(firstDayOfWeek, 6),
    };
};

export const getMyCalendarCaption = ({ dashboardData }) => {
    const actionNeededPendingInterviews = _defaultTo(_get(dashboardData, 'actionNeededPendingInterviews'), []);
    const actionNeededRescheduledInterviews = _defaultTo(_get(dashboardData, 'actionNeededRescheduledInterviews'), []);
    const unreadConfirmedInterviews = _defaultTo(_get(dashboardData, 'unreadConfirmedInterviews'), []);
    const unreadDeclinedInterviews = _defaultTo(_get(dashboardData, 'unreadDeclinedInterviews'), []);

    return actionNeededPendingInterviews.length + actionNeededRescheduledInterviews.length + unreadConfirmedInterviews.length + unreadDeclinedInterviews.length;
};

export const getIsShowCalendarTileBatch = ({ interviewId, dashboardData }) => {
    const actionNeededPendingInterviews = _defaultTo(_get(dashboardData, 'actionNeededPendingInterviews'), []);
    const actionNeededRescheduledInterviews = _defaultTo(_get(dashboardData, 'actionNeededRescheduledInterviews'), []);
    const unreadConfirmedInterviews = _defaultTo(_get(dashboardData, 'unreadConfirmedInterviews'), []);
    const unreadDeclinedInterviews = _defaultTo(_get(dashboardData, 'unreadDeclinedInterviews'), []);

    return _includes([...actionNeededPendingInterviews, ...actionNeededRescheduledInterviews, ...unreadConfirmedInterviews, ...unreadDeclinedInterviews], interviewId);
};

export const getIsShowCalendarMoreTileBatch = ({ interviewList, dashboardData }) => {
    const actionNeededPendingInterviews = _defaultTo(_get(dashboardData, 'actionNeededPendingInterviews'), []);
    const actionNeededRescheduledInterviews = _defaultTo(_get(dashboardData, 'actionNeededRescheduledInterviews'), []);
    const unreadConfirmedInterviews = _defaultTo(_get(dashboardData, 'unreadConfirmedInterviews'), []);
    const unreadDeclinedInterviews = _defaultTo(_get(dashboardData, 'unreadDeclinedInterviews'), []);
    const interviewIds = [...actionNeededPendingInterviews, ...actionNeededRescheduledInterviews, ...unreadConfirmedInterviews, ...unreadDeclinedInterviews];

    return _some(interviewList, o => _includes(interviewIds, o.interviewId));
};
